import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import {createTheme, styled, ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {MainListItems} from './listItems';
import {
    AccessTime,
    AddTask,
    Assignment,
    CalendarMonth,
    CloudUpload,
    History,
    LocalAtm,
    Logout,
    QuestionMark,
    SpeakerNotes,
    SpeakerNotesOff,
    WarningAmber
} from '@mui/icons-material';
import {Hidden, ListItemButton, ListItemIcon, Tooltip} from '@mui/material';
import AuthService from '../../services/AuthService';
import authService from '../../services/AuthService';
import {useLocation, useNavigate} from 'react-router-dom';
import projectService from '../../services/ProjectService';
import payrollService from '../../services/PayrollService';
import {TitleContext} from './TitleContext';
import GeneralFileUploadDialog from './GeneralFileUploadDialog';
import roleService from "../../services/RoleService";
import taskLogService from "../../services/TaskLogService";
import {useSnackbar} from "../../util/SnackbarProvider";
import TaskAssignmentDialog from "./TaskAssignmentDialog";
import StickyNotes from "../sticky-notes/StickyNotes";
import taskService from "../../services/TaskService";
import {LocalNavigationContext} from "./LocalNavigationContext";

const drawerWidth = 260;
const defaultTheme = createTheme();
const backgroundMap = {
    0: "lightcyan",
    1: "lightcyan",
    2: "lightcyan",
    3: "lightcyan",
    4: "#87CEEB",
    5: "#2e77bf",
    6: "#2e77bf",
    7: "#2e77bf"
};

const VERSION = 'v1.4.0_0107'

export default function Dashboard({content}) {

    const [open, setOpen] = useState(localStorage.getItem('menu-state') === "true" || false);
    const generalFileUploadRef = React.useRef();
    const taskAssignmentDialog = React.useRef();
    const {title, text} = useContext(TitleContext);
    const {localNavigation} = useContext(LocalNavigationContext)
    const navigate = useNavigate()
    const username = authService.getCurrentUser()?.username
    const {sendAlertMessage} = useSnackbar()
    const [showStickyNotes, setShowStickyNotes] = useState(localStorage.getItem('sticky-note-state') !== "false");

    const depth = window.location.pathname.split('/').length
    const background = backgroundMap[depth] || "lightcyan";

    const [lastTenPages, setLastTenPages] = useState(JSON.parse(localStorage.getItem('history')) || []);
    const location = useLocation();

    useEffect(() => {
        setLastTenPages((prevPages) => {
            const currentPath = text;
            if (text === '') return prevPages;
            const pageExists = prevPages.some((page) => page.url === location.pathname);
            let updatedPages = pageExists ? prevPages.filter((page) => page.url !== location.pathname) : prevPages;
            updatedPages = [...updatedPages, {name: currentPath, url: location.pathname}];
            let result = updatedPages.length > 10 ? updatedPages.slice(1) : updatedPages;
            localStorage.setItem('history', JSON.stringify(result));
            return result
        });
    }, [location.pathname, text, title]);

    const toggleDrawer = () => {
        localStorage.setItem('menu-state', !open);
        setOpen(!open);
    };

    useEffect(() => {
        if (authService.getCurrentUser().role === 'ROLE_GUEST') return
        taskLogService.getUnfilledDatesOfUser()
            .then(response => {
                if (response.length > 0) {
                    const text = response.map(item => <>{item} <br/></>)
                    sendAlertMessage(<>{'Hiányzó munkaidőbejegyzés'}<br/> {text}</>, 'warning')
                }
            })
            .catch(error => console.error(error));
    }, [content])

    const [unseenTaskCount, setUnseenTaskCount] = useState(0);
    useEffect(() => {
        const getUnseenTaskCount = () => {
            taskService.getUnseenTaskCount()
                .then(response => setUnseenTaskCount(response))
                .catch(error => console.error(error));
        };
        getUnseenTaskCount();
        const intervalId = setInterval(getUnseenTaskCount, 5 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    const [markedTaskCount, setMarkedTaskCount] = useState(0);
    useEffect(() => {
        const getMarkedTaskCount = () => {
            taskService.getMarkedTaskCount()
                .then(response => setMarkedTaskCount(response))
                .catch(error => console.error(error));
        };
        getMarkedTaskCount();
        const intervalId = setInterval(getMarkedTaskCount, 5 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    const [permissions, setPermissions] = useState([]);
    useEffect(() => {
        roleService.getCurrentPermissions()
            .then(response => setPermissions(response))
            .catch(error => console.error(error));
    }, [content])

    const [projects, setProjects] = useState({});
    useEffect(() => {
        if (authService.getCurrentUser().role === 'ROLE_GUEST') return
        projectService.getImportantProjects()
            .then(response => setProjects(response))
            .catch(error => console.error(error));
    }, [content])

    const [unpaidSalariesCount, setUnpaidSalariesCount] = useState(0);
    useEffect(() => {
        if (!permissions.includes('PAYROLL_READER')) return
        payrollService.getUnpaidSalariesCount()
            .then(response => setUnpaidSalariesCount(response))
            .catch(error => console.error(error))
    }, [permissions, content])

    const toggleStickNoteState = () => {
        localStorage.setItem('sticky-note-state', !showStickyNotes)
        setShowStickyNotes(!showStickyNotes)
    }

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box sx={{display: 'flex'}}>
                <CssBaseline/>
                <AppBar position="absolute" open={open}>
                    <Toolbar sx={{pr: '24px'}}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && {display: 'none'}),
                            }}
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{flexGrow: 0, width: '32vw', overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipse"}}
                        >
                            {title}
                        </Typography>

                        <Hidden lgDown>
                            <span style={{flexGrow: 1}}>
                                {authService.getCurrentUser().role !== 'ROLE_GUEST' &&
                                    <>
                                        <Tooltip title={"Munkaidő"}>
                                            <IconButton color="inherit" onClick={() => navigate("/dashboard/task-logs")}>
                                                <AccessTime sx={{fontSize: "35px"}}/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={"Naptár"}>
                                            <IconButton color="inherit" onClick={() => navigate("/dashboard/project-tasks/calendar")}>
                                                <CalendarMonth sx={{fontSize: "35px"}}/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={"Feladatok"}>
                                            <IconButton color="inherit" onClick={() => navigate("/dashboard/tasks")} >
                                                <Badge badgeContent={unseenTaskCount} color="secondary" anchorOrigin={{vertical: 'top'}} sx={{'&>span:last-child': {top: '0px', right: '5px'}}}>
                                                    <Badge badgeContent={markedTaskCount} anchorOrigin={{vertical: 'bottom'}} sx={{'& span': {backgroundColor: 'orange', color: 'black', bottom: '5px', right: '5px'}}}>
                                                        <Assignment sx={{fontSize: "35px"}}/>
                                                    </Badge>
                                                </Badge>
                                            </IconButton>
                                        </Tooltip>
                                    </>}
                                {localNavigation}
                            </span>
                        </Hidden>
                        <Typography sx={{marginRight: "10px", cursor: "pointer"}} onClick={() => navigate("/dashboard/profile")}>
                            {username}
                        </Typography>
                        <Tooltip title="Súgó">
                            <IconButton color="inherit" href="/help/index.html" component="a" target="_blank" rel="noopener noreferrer">
                                <QuestionMark/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Értesítések (Hamarosan)">
                            <IconButton color="inherit">
                                <NotificationsIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={showStickyNotes ? "Jegyzetek elrejtése" : "Jegyzetek megjelenítése"}>
                            <IconButton color="inherit" onClick={() => toggleStickNoteState()}>
                                {showStickyNotes ? <SpeakerNotes/> : <SpeakerNotesOff/>}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Dokumentumok Feltöltése">
                            <IconButton color="inherit" onClick={() => {
                                generalFileUploadRef.current.openDialog()
                            }}>
                                <CloudUpload/>
                            </IconButton>
                        </Tooltip>
                        {authService.getCurrentUser().role !== 'ROLE_GUEST' &&
                            <Tooltip title="Feladat Kiosztása">
                                <IconButton color="inherit" onClick={() => {
                                    taskAssignmentDialog.current.openDialog()
                                }}>
                                    <AddTask/>
                                </IconButton>
                            </Tooltip>}
                        {permissions.includes('PAYROLL_READER') &&
                            <IconButton color="inherit" onClick={() => {
                                const now = new Date()
                                const month = now.getMonth() === 0 ? 11 : now.getMonth();
                                const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
                                localStorage.setItem("payroll-year", year)
                                localStorage.setItem("payroll-month", month)
                                navigate("/dashboard/payrolls")
                            }}>
                                <Tooltip title="Kifizetésre vár">
                                    <Badge badgeContent={unpaidSalariesCount} color="secondary">
                                        <LocalAtm/>
                                    </Badge>
                                </Tooltip>
                            </IconButton>
                        }
                        {permissions.includes('PROJECT_READER') &&
                            <IconButton color="inherit" onClick={() => {
                                localStorage.setItem("project-search-params", "{\"priority\":\"EXTRA_IMPORTANT\"}")
                                navigate("/dashboard/projects")
                            }}>
                                <Tooltip title={
                                    <span>{'Projektek'}
                                        <br/>
                                        {'Extra Fontos: ' + projects.extraImportant}
                                        <br/>
                                        {'Fontos: ' + projects.important}
                                </span>}>
                                    <Badge badgeContent={projects.extraImportant} color="secondary">
                                        <WarningAmber/>
                                    </Badge>
                                </Tooltip>
                            </IconButton>
                        }
                        <IconButton color="inherit" onClick={() => {
                            AuthService.logout();
                            navigate("/login")
                        }}>
                            <Tooltip title="Kijelentkezés">
                                <Badge color="secondary">
                                    <Logout/>
                                </Badge>
                            </Tooltip>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Drawer id="drawer" variant="permanent" open={open}>
                    <Toolbar>
                        <IconButton color="inherit" sx={{marginLeft: "-12px", fontSize: "20px"}}
                                    onClick={() => navigate("/dashboard/change-logs")}>
                            {VERSION}
                        </IconButton>
                        <IconButton onClick={toggleDrawer} sx={{marginLeft: "auto"}}>
                            <ChevronLeftIcon/>
                        </IconButton>
                    </Toolbar>
                    <Divider/>
                    <List component="nav" sx={{overflowY: 'scroll', maxHeight: "65vh"}}>
                        {MainListItems(permissions)}
                    </List>
                    <List sx={{marginTop: "auto", backgroundColor: "lightgray"}}>
                        {lastTenPages.slice().reverse().map((page, index) => (
                            <Tooltip title={page.url} placement='right' key={index}>
                                <ListItemButton key={index} onClick={() => navigate(page.url)} sx={{height: "25px"}}>
                                    <ListItemIcon>
                                        <History/>
                                    </ListItemIcon>
                                    <span style={{fontSize: '12px', marginLeft: "-24px"}}>{page.name}</span>
                                </ListItemButton>
                            </Tooltip>
                        ))}
                    </List>
                </Drawer>
                <Box component="main" id="main" sx={{
                    flexGrow: 1,
                    height: '100vh',
                    padding: '84px 20px 0px 20px',
                    overflow: 'auto',
                    background: background
                }}>
                    {content}
                </Box>
            </Box>
            <GeneralFileUploadDialog ref={generalFileUploadRef}/>
            <TaskAssignmentDialog ref={taskAssignmentDialog}/>
            {showStickyNotes && <StickyNotes toggleStickNoteState={toggleStickNoteState}/>}
        </ThemeProvider>
    );
}

const AppBar = styled(MuiAppBar, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(['width', 'margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    }));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: 0,
            }),
        },
    }),
);
